// Provide access to URL parameters for any consumer
import React, { useContext, useEffect, useState } from 'react';

import { useSearchParams } from 'next/navigation';

import { fromPairs } from 'lodash';

// List of all the expected URL params
const urlParams = [
  // Main Layout Params
  'hideHeader',
  'isEmbedded',
  // Dispatch Scheduler URL Params:
  'schedule',
  'date',
  'operators',
  'sortBy',
  'order',
  'filters',
  'view',
  'pinned',
  // Ticket Search URL Params:
  'tsStartDate',
  'tsEndDate',
  'tsFilters',
  'tsQuery',
] as const;
export type URLParams = (typeof urlParams)[number];

type URLParamsContextType = {
  updateParam: (param: URLParams, value: string) => void;
} & Record<URLParams, string>;

export const URLParamsContext = React.createContext<URLParamsContextType>(
  Object.assign(
    { updateParam: () => null },
    fromPairs(urlParams.map((p) => [p, ''])) as Record<URLParams, string>
  )
);

export const useURLParamsContext = () => useContext(URLParamsContext);

export const URLParamsContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const searchParams = useSearchParams();
  const [paramsState, setParamsState] = useState<Record<URLParams, string>>(
    () => fromPairs(urlParams.map((p) => [p, ''])) as Record<URLParams, string>
  );

  useEffect(() => {
    const newParamsState = {} as Record<URLParams, string>;
    urlParams.forEach((param) => {
      newParamsState[param] = searchParams.get(param) || '';
    });
    setParamsState(newParamsState);
  }, []);

  function updateParam(param: URLParams, value: string) {
    const url = new URL(window.location.href);
    if (!value) {
      url.searchParams.delete(param);
    } else {
      url.searchParams.set(param, value);
    }
    window.history.pushState({}, '', url);
    // Also update the local state
    setParamsState((prevState) => ({
      ...prevState,
      [param]: value,
    }));
  }

  return (
    <URLParamsContext.Provider
      value={{
        updateParam,
        ...paramsState,
      }}
    >
      {children}
    </URLParamsContext.Provider>
  );
};
